import React, {useContext, useState, useMemo, useEffect, useRef} from 'react';
import {
    Button,
    TextField,
    Typography,
    Container,
    Paper,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Tabs, Tab, MenuItem, Select,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {getQueryParamValue, isMobile, parseJSON} from "../App/utils";
import {CodeViewer} from "../Code/CodeViewer";
import {JSONComponent} from "../ContentWidgets/JSONComponent";

import {signKBTransactionJWT} from "../Crypto/utils";
import { AlertContext } from "../App/App";
import {colorText} from "../App/theme";
import {MyServices} from "../constants";
import {playAudio, TextToSpeechPlayer} from "../Chat/TextToSpeechPlayer";
import {ServiceInterfaces} from "../Components/ServiceInterfaces";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import {Link, useNavigate} from "react-router-dom";
import {CopyToClipboard} from "react-copy-to-clipboard";
import {getKB} from "../API/KB/KBAPI";
import {isWindows} from "../utils";
import {GAEvent} from "../App/GA";
import {renderServicePrices} from "./Pricing";

const processInputValue = (param) => {
    let value = param.value;
    if (param.value === null) return null;

    if (param?.type === 'int') return parseInt(param.value)

    if (value === 'false') return false;
    if (value === 'true') return true;

    let parsedData = parseJSON(value);

    if (Array.isArray(parsedData)) return parsedData;

    return value;
}

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography component="pre">{children}</Typography>
                </Box>
            )}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const generateAPIKeySnippet = (endpoint, headers) => {
    if (!endpoint?.transactionJWT) return null;

    // const method = endpoint.category === 'Chat' ? endpoint.method : "POST";
    const method = endpoint.method;
    let snippet = `curl -X ${method} "${endpoint.url}`;

    // Handling GET parameters
    if (method === "GET" && endpoint.params && endpoint.params.length > 0) {
        snippet += '?' + [...endpoint.params,{ name:'transactionJWT', value: endpoint.transactionJWT }]
            .map(param => `${encodeURIComponent(param.name)}=${encodeURIComponent(param.value)}`).join('&');
    }
    snippet += `"`; // Close the initial curl command

    // Adding the transaction-jwt header on a new line
    if (method === "POST" ) snippet += ` \\\n  -H "transaction-jwt: ${endpoint.transactionJWT}"`;

    // Adding other headers, each on a new line
    for (const [key, value] of Object.entries(headers)) {
        snippet += ` \\\n  -H "${key}: ${value}"`;
    }

    // Handling POST parameters
    if (method === "POST" && endpoint.params) {
        const body = endpoint.params.reduce((acc, param) => {
            const value = processInputValue(param);
            if (value !== null) {
                acc[param.name] = value;
            }
            return acc;
        }, {});
        const bodyString = JSON.stringify(body).replace(/'/g, "'\\''");
        snippet += ` \\\n  -d '${bodyString}'`;
    }

    // Handling image type endpoint
    const rnd = randomNumber?.toString()?.substring(0,5);
    if (endpoint.outputType === 'image') {
        snippet += ` \\\n  --output image_${rnd}.png`;
        snippet += isWindows() ? ` && start image_${rnd}.png` : ` && open image_${rnd}.png`;
    }

    return snippet;
};

const generatePythonSnippet = (endpoint, headers) => {
    let snippet = `import requests\n\n`;

    // URL construction for GET requests with parameters
    if (endpoint.method === "GET" && endpoint.params && endpoint.params.length > 0) {
        const params = endpoint.params.map(param => `${encodeURIComponent(param.name)}=${encodeURIComponent(param.value)}`).join('&');
        snippet += `url = "${endpoint.url}?${params}"\n`;
    } else {
        snippet += `url = "${endpoint.url}"\n`;
    }

    // Headers
    snippet += `headers = {\n`;
    for (const [key, value] of Object.entries(headers)) {
        snippet += `    "${key}": "${value}",\n`;
    }
    snippet += `}\n\n`;

    // Additional data for POST requests
    if (endpoint.method === "POST" && endpoint.params) {
        const body = endpoint.params.reduce((acc, param) => {
            acc[param.name] = processInputValue(param);
            return acc;
        }, {});
        snippet += `response = requests.post(url, headers=headers, json=${JSON.stringify(body, null, 4)})\n`;
    } else {
        snippet += `response = requests.get(url, headers=headers)\n`;
    }

    // Response handling
    snippet += `\nprint(response.text)\n`;

    return snippet;
};

const generateBashSnippet = (endpoint, headers) => {
    let snippet = `curl -X ${endpoint.method} "${endpoint.url}`;

    // Handling GET parameters
    if (endpoint.method === "GET" && endpoint.params && endpoint.params.length > 0) {
        snippet += '?' + endpoint.params.map(param => `${encodeURIComponent(param.name)}=${encodeURIComponent(param.value)}`).join('&');
    }
    snippet += `"`; // Close the initial curl command

    // Adding the transaction-jwt header on a new line
    snippet += ` \\\n  -H "transaction-jwt: $(openkbs sign -a ${endpoint.accountId})"`;

    // Adding other headers, each on a new line
    for (const [key, value] of Object.entries(headers)) {
        snippet += ` \\\n  -H "${key}: ${value}"`;
    }

    // Handling POST parameters
    if (endpoint.method === "POST" && endpoint.params) {
        const body = endpoint.params.reduce((acc, param) => {
            const value = processInputValue(param);
            if (value !== null) {
                acc[param.name] = value;
            }
            return acc;
        }, {});
        const bodyString = JSON.stringify(body).replace(/'/g, "'\\''");
        snippet += ` \\\n  -d '${bodyString}'`;
    }

    // Handling image type endpoint
    const rnd = randomNumber?.toString()?.substring(0,5);
    if (endpoint.outputType === 'image') {
        snippet += ` \\\n  --output image_${rnd}.png`;
        snippet += isWindows() ? ` && start image_${rnd}.png` : ` && open image_${rnd}.png`;
        // snippet += ` \\\n  > image_${rnd}.png && open image_${rnd}.png`;
    }

    return snippet;
};

// Helper function to generate the JavaScript snippet
const generateJSSnippet = (endpoint, headers) => {
    let snippet = `fetch("${endpoint.url}`;

    if (endpoint.method === "GET" && endpoint.params && endpoint.params.length > 0) {
        snippet += '?' + endpoint.params.map(param => `${encodeURIComponent(param.name)}=${encodeURIComponent(param.value)}`).join('&');
    }
    snippet += `", {\n  method: "${endpoint.method}",\n  headers: {\n`;

    for (const [key, value] of Object.entries(headers)) {
        snippet += `    "${key}": "${value}",\n`;
    }

    snippet += `  }`;

    if (endpoint.method === "POST" && endpoint.params) {
        const body = endpoint.params.reduce((acc, param) => {
            acc[param.name] = processInputValue(param);
            return acc;
        }, {});
        snippet += `,\n  body: JSON.stringify(${JSON.stringify(body, null, 2)})`;
    }

    snippet += `\n})\n.then(response => response.json())\n.then(data => console.log(data))\n.catch(error => console.error('Error:', error));`;

    return snippet;
};

let transformModelsToArrayData;
const transformModelsToArray = (modelsObject) => {
    const initSelectedService = getQueryParamValue('selectedService')?.replace(/-/g, ' ')

    if (!transformModelsToArrayData) {
        transformModelsToArrayData = Object.keys(modelsObject).map(key => {
            const model = modelsObject[key];
            const params = {...model, loading: false};
            if (initSelectedService) {
                params.expanded = initSelectedService === model?.name?.toLowerCase() ? true : false
            }
            return params
        }).filter(o => o.category && o.category !== 'Database');
    }

    return transformModelsToArrayData;
};

const initTokenTypeValue = isMobile ? 12 : parseInt(getQueryParamValue('tokenType'))
const randomNumber = (Math.floor(Math.random() * 10**15) + 1);

const handleOnCopy = () => {
    GAEvent("try_api_service", { value: 10 })
}

const loadedAt = +new Date();
export const APIEndpoints = ({userProfile}) => {
    const { setSystemAlert } = useContext(AlertContext);
    const [endpoints, setEndpoints] = useState(transformModelsToArray(MyServices));
    const [paramsChanged, setParamsChanged] = useState(false);
    const [tokenTypeValue, setTokenTypeValue] = useState(initTokenTypeValue || 0);
    const accordionRefs = useRef({});

    useEffect(() => {
        if (!userProfile?.accountId) return;
        const initSelectedService = getQueryParamValue('selectedService')?.replace(/-/g, ' ')
        const endpoint = endpoints.find(e => e.name?.toLowerCase() === initSelectedService);

        if (initTokenTypeValue) {
            handleChange(null, initTokenTypeValue, endpoint)
        }

    }, [userProfile])

    const endpointsByCategory = useMemo(() => {
        return endpoints.reduce((acc, endpoint) => {
            if (!acc[endpoint.category]) {
                acc[endpoint.category] = [];
            }
            acc[endpoint.category].push(endpoint);
            return acc;
        }, {});
    }, [endpoints]);

    const headers = {
        'Content-Type': 'application/json'
    }

    const handleChange = async (event, newValue, endpoint) => {
        setTokenTypeValue(newValue);

        if (newValue === 1 && !endpoint?.transactionJWT) {
            const { accountId, walletPublicKey, walletPrivateKey } = userProfile;
            const transactionJWT = await signKBTransactionJWT({
                accountId,
                walletPublicKey,
                walletPrivateKey,
                toAccountId: endpoint.accountId,
                unlimitedTransactions: true,
                expiresInSeconds: 60 * 60 * 24 * 365 * 100
            });

            // Update the endpoints state
            setEndpoints(prevEndpoints =>
                prevEndpoints.map(ep =>
                    ep.accountId === endpoint.accountId ? { ...ep, transactionJWT } : ep
                )
            );
        }


    };

    const handleInputChange = (endpointId, paramName, value) => {
        setParamsChanged(true);
        setEndpoints(endpoints.map(endpoint => {
            if (endpoint.name === endpointId) {
                endpoint.params = endpoint.params.map(param => {
                    if (param.name === paramName) {
                        param.value = value;
                    }
                    return param;
                });
            }
            return endpoint;
        }));
    };

    const handleSendRequest = async (endpoint) => {
        GAEvent("try_api_service", { value: 3 })

        setEndpoints(endpoints.map(ep => {
            if (ep.name === endpoint.name) {
                ep.loading = true;
            }
            return ep;
        }));

        let url = endpoint.url;

        if (endpoint.method === "GET" && endpoint.params && endpoint.params.length > 0) {
            let urlObj = new URL(url);
            let searchParams = new URLSearchParams();
            for (let param of endpoint.params) searchParams.append(param.name, processInputValue(param));
            urlObj.search = searchParams.toString();
            url = urlObj.toString();
        }

        const {accountId, walletPublicKey, walletPrivateKey} = userProfile;
        // console.log(await signKBTransactionJWT({accountId, walletPublicKey, walletPrivateKey, toAccountId: endpoint.accountId}))
        //
        // return;
        const myHeaders = {
            'transaction-jwt': await signKBTransactionJWT({accountId, walletPublicKey, walletPrivateKey, toAccountId: endpoint.accountId}),
            ...headers
        }


        let response;

        try {
            response = await fetch(url, {
                method: endpoint.method,
                headers: myHeaders,
                body: endpoint.method === "POST"
                    ? JSON.stringify(endpoint.params.reduce((acc, param) => {
                        acc[param.name] = processInputValue(param);
                        return acc;
                    }, {}))
                    : null,
            })
        } catch (e) {
            response = {error: e.message}
        }

        setParamsChanged(false);

        if (response?.headers?.get("Content-Type").startsWith("image")) {
            const imageBlob = await response.blob();

            const imageUrl = URL.createObjectURL(imageBlob);
            setEndpoints(endpoints.map(ep => {
                if (ep.name === endpoint.name) {
                    ep.response = imageUrl;
                    ep.loading = false;
                    ep.isImage = true;
                }
                return ep;
            }));
            return
        }

        // Handle non-image responses as before
        const data = await response?.json?.() || response;

        if (data?.audioContent) playAudio({ audioContent: data?.audioContent })

        setEndpoints(endpoints.map(ep => {
            if (ep.name === endpoint.name) {
                ep.response = data;
                ep.loading = false;
            }
            return ep;
        }));

    };

    const handleAccordionChange = (panelId) => (event, isExpanded) => {
        setTokenTypeValue(isMobile ? 12 : 0);
        setEndpoints(endpoints.map(endpoint => {
            if (endpoint.name === panelId) {
                endpoint.expanded = isExpanded;
            }
            return endpoint;
        }));
    };

    useEffect(() => {
        if (+new Date() - loadedAt > 2000) return;
        const expandedEndpoint = endpoints.find(endpoint => endpoint.expanded);
        if (expandedEndpoint && accordionRefs.current[expandedEndpoint.name]) {
            const element = accordionRefs.current[expandedEndpoint.name];
            element.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [endpoints]);

    const colors = {
        'Chat' : 'secondary.main',
        'Audio' : 'secondary.main',
        'Visual' : 'secondary.main',
        'Web' : 'secondary.main',
        'Embedding' : 'secondary.main',
    }
    const navigate = useNavigate();


    return (
        <Container maxWidth={isMobile ? 'xl' : 'lg'} sx={{ maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}>
            {Object.entries(endpointsByCategory).map(([category, endpoints], index, array) => (
                <React.Fragment key={category}>
                    {(
                        <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                            <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'divider' }} />
                            <Box sx={{ mx: 2 }}>
                                <Typography variant="h6" component="span" color={colors?.[category] || colorText}>
                                    {category === 'Visual' ? 'Image Generation' : category}
                                </Typography>
                            </Box>
                            <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'divider' }} />
                        </Box>
                    )}
                    {endpoints.filter(o => !o?.exclude).map(endpoint => {
                        if (!endpoint?.url) return null;
                        const snippetCodeStaticToken = generateAPIKeySnippet(endpoint, headers);

                        return (
                            <Accordion key={endpoint.name} sx={{ my: 3}}
                                       style={{marginBottom: endpoint?.expanded ? '40px' : undefined}}
                                       expanded={endpoint.expanded}
                                       onChange={handleAccordionChange(`${endpoint.name}`)}
                                       >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}
                                                  style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{ flex: 1 }}>
                                        <Typography style={{ textAlign: 'left', paddingLeft: '10px' }}>
                                            {endpoint.name}
                                        </Typography>
                                    </div>
                                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                        <Box style={{paddingLeft: '26px'}}><ServiceInterfaces endpoint={endpoint} /></Box>
                                    </div>
                                    {!isMobile && <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                    </div>}
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Box style={{paddingLeft: '10px', textAlign: 'left'}}>{endpoint?.serviceDescription} <br /><br /> <Typography fontSize={14}>{renderServicePrices(endpoint)}</Typography></Box>
                                        {/*<Box sx={{ width: '100%' }}>*/}
                                        {/*    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>*/}
                                        {/*        <Tabs value={tokenTypeValue} onChange={(e,v) => handleChange(e,v,endpoint)} aria-label="basic tabs example">*/}
                                        {/*            <Tab style={{textTransform: 'none'}} label="Secure CLI API" {...a11yProps(0)} />*/}
                                        {/*            <Tab style={{textTransform: 'none'}} label="Static Token API" {...a11yProps(3)} />*/}
                                        {/*            /!*<Tab label="JavaScript" {...a11yProps(1)} />*!/*/}
                                        {/*            /!*<Tab label="Python" {...a11yProps(2)} />*!/*/}
                                        {/*        </Tabs>*/}
                                        {/*    </Box>*/}
                                        {/*    <TabPanel value={tokenTypeValue} index={0}>*/}
                                        {/*        <Typography fontSize={14} sx={{ maxWidth: '100%', textAlign: 'left', wordBreak: 'break-word', whiteSpace: 'normal' }}>*/}
                                        {/*            Secure CLI generates a one-time secret transactionJWT for each API call, ensuring high security.*/}
                                        {/*            <Button*/}
                                        {/*                style={{ marginLeft: 0 }}*/}
                                        {/*                variant="text"*/}
                                        {/*                color="secondary"*/}
                                        {/*                component="a"*/}
                                        {/*                href="/api/cli"*/}
                                        {/*                target="_blank"*/}
                                        {/*                rel="noopener noreferrer"*/}
                                        {/*            >*/}
                                        {/*                Requires CLI*/}
                                        {/*            </Button>*/}
                                        {/*        </Typography>*/}
                                        {/*        <Box sx={{ display: 'flex', alignItems: 'center' }}>*/}
                                        {/*            <CodeViewer*/}
                                        {/*                setSystemAlert={setSystemAlert}*/}
                                        {/*                style={{ paddingRight: isMobile ? undefined : '45px' }}*/}
                                        {/*                source={generateBashSnippet(endpoint, headers)}*/}
                                        {/*                onCopy={handleOnCopy}*/}
                                        {/*                language={"bash"}*/}
                                        {/*                limitedWidth={true}*/}
                                        {/*                className="codeWrapperAPI"*/}
                                        {/*            />*/}
                                        {/*        </Box>*/}
                                        {/*    </TabPanel>*/}
                                        {/*    <TabPanel value={tokenTypeValue} index={1}>*/}
                                        {/*        <Typography fontSize={14} sx={{ maxWidth: '100%', textAlign: 'left', wordBreak: 'break-word', whiteSpace: 'normal'}}>*/}
                                        {/*            Static Token provides unlimited access to this endpoint.*/}
                                        {/*        </Typography>*/}
                                        {/*        {snippetCodeStaticToken ? <Box sx={{ display: 'flex', alignItems: 'center' }}>*/}
                                        {/*            <CodeViewer*/}
                                        {/*                setSystemAlert={setSystemAlert}*/}
                                        {/*                style={{ paddingRight: isMobile ? undefined : '45px' }}*/}
                                        {/*                source={snippetCodeStaticToken}*/}
                                        {/*                onCopy={handleOnCopy}*/}
                                        {/*                noCopy={false}*/}
                                        {/*                limitedWidth={true}*/}
                                        {/*                language={"bash"}*/}
                                        {/*                className="codeWrapperAPI"*/}
                                        {/*            />*/}
                                        {/*        </Box> : <CircularProgress style={{marginTop: 20}} />}*/}
                                        {/*    </TabPanel>*/}
                                        {/*    /!*<TabPanel tokenTypeValue={tokenTypeValue} index={1}>*!/*/}
                                        {/*    /!*    <Box sx={{ display: 'flex', alignItems: 'center' }}>*!/*/}
                                        {/*    /!*        <CodeViewer*!/*/}
                                        {/*    /!*            setSystemAlert={setSystemAlert}*!/*/}
                                        {/*    /!*            style={{ paddingRight: isMobile ? undefined : '45px' }}*!/*/}
                                        {/*    /!*            source={generateJSSnippet(endpoint, headers)}*!/*/}
                                        {/*    /!*            noCopy={true}*!/*/}
                                        {/*    /!*            limitedWidth={true}*!/*/}
                                        {/*    /!*            language={"javascript"}*!/*/}
                                        {/*    /!*            className="codeWrapperAPI"*!/*/}
                                        {/*    /!*        />*!/*/}
                                        {/*    /!*    </Box>*!/*/}
                                        {/*    /!*</TabPanel>*!/*/}
                                        {/*    /!*<TabPanel tokenTypeValue={tokenTypeValue} index={2}>*!/*/}
                                        {/*    /!*    <Box sx={{ display: 'flex', alignItems: 'center' }}>*!/*/}
                                        {/*    /!*        <CodeViewer*!/*/}
                                        {/*    /!*            setSystemAlert={setSystemAlert}*!/*/}
                                        {/*    /!*            style={{ paddingRight: isMobile ? undefined : '45px' }}*!/*/}
                                        {/*    /!*            source={generatePythonSnippet(endpoint, headers)}*!/*/}
                                        {/*    /!*            noCopy={true}*!/*/}
                                        {/*    /!*            limitedWidth={true}*!/*/}
                                        {/*    /!*            language={"python"}*!/*/}
                                        {/*    /!*            className="codeWrapperAPI"*!/*/}
                                        {/*    /!*        />*!/*/}
                                        {/*    /!*    </Box>*!/*/}
                                        {/*    /!*</TabPanel>*!/*/}
                                        {/*</Box>*/}

                                        {endpoint.params.map(param => (
                                            param.options && Array.isArray(param.options) ? (
                                                <FormControl key={param.name} variant="outlined" fullWidth>
                                                    <InputLabel>{param.name}</InputLabel>
                                                    <Select
                                                        label={param.name}
                                                        value={param.value}
                                                        onChange={(e) => handleInputChange(endpoint.name, param.name, e.target.value)}
                                                        sx={{
                                                            textAlign: 'left',
                                                            '& .MuiSelect-select': { // Targeting the inner select element
                                                                alignItems: 'flex-start', // Align items to the start (left for LTR languages)
                                                            }
                                                        }}
                                                    >
                                                        {param.options.map(option => (
                                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            ) : (
                                                <TextField
                                                    key={param.name}
                                                    label={param.name}
                                                    variant="outlined"
                                                    value={param.value}
                                                    onChange={(e) => handleInputChange(endpoint.name, param.name, e.target.value)}
                                                    fullWidth
                                                />
                                            )
                                        ))}

                                        {endpoint?.modelType === 'inpaint' && (
                                            <Box display="flex" justifyContent="space-around" flexDirection={isMobile ? 'column' : 'row'} alignItems="center">
                                                <img width={256} src={endpoint.params.find(param => param.name === 'image')?.value} alt="Image" />
                                                <img width={256} src={endpoint.params.find(param => param.name === 'mask_image')?.value} alt="Mask Image" />
                                                {<img width={256} src={endpoint.response} alt="Response" style={{visibility: !endpoint.response ? 'hidden' : undefined }}/>}
                                            </Box>
                                        )}

                                        {endpoint?.modelType === 'upscale' && (
                                            <Box display="flex" justifyContent="space-around" flexDirection={isMobile ? 'column' : 'row'} alignItems="center">
                                                <img width={256} src={endpoint.params.find(param => param.name === 'image')?.value} alt="Image" />
                                                {<img width={256} src={endpoint.response} alt="Response" style={{visibility: !endpoint.response ? 'hidden' : undefined }}/>}
                                            </Box>
                                        )}

                                        <Button variant="contained"

                                                style={{
                                                    opacity: 0.85
                                                }}
                                                color={colors[category]?.split('.')[0]} onClick={() => handleSendRequest(endpoint)} disabled={endpoint.loading}>
                                            {endpoint.loading ? <CircularProgress size={24} /> : 'Try ' + endpoint.name}
                                        </Button>

                                        <div ref={el => accordionRefs.current[endpoint.name] = el}></div>

                                        {endpoint?.response?.audioContent && <Box
                                            style={{visibility: paramsChanged ? 'hidden' : undefined}}
                                        >
                                            <TextToSpeechPlayer timeout={200} audioContent={endpoint?.response?.audioContent} />
                                        </Box>}


                                        {endpoint.response &&
                                            (endpoint.isImage ?
                                                (endpoint?.modelType !== 'inpaint' && endpoint?.modelType !== 'upscale' &&
                                                    <img src={endpoint.response} alt="Response" />)
                                                : (
                                                    <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
                                                        <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                                                            <JSONComponent data={endpoint.response} props={{enableClipboard: false}} collapsed={false}/>
                                                        </Typography>
                                                    </Paper>
                                                ))}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        )
                    })}
                </React.Fragment>
            ))}
        </Container>
    );
};